import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Eye, Calendar, Building2, Users } from "lucide-react";
import { bondTrusteesAPI, formatDate } from "../../services/api";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { createResponseSelector } from "../../utils/apiHelpers";
import { useAuth } from "../../contexts/AuthContext";

const STATUS_CONFIG = {
  Draft: { className: "bg-gray-100 text-gray-800", icon: "📝" },
  "Pending L1": { className: "bg-yellow-100 text-yellow-800", icon: "①" },
  "Pending L2": { className: "bg-yellow-100 text-yellow-800", icon: "②" },
  "Pending Review": { className: "bg-yellow-100 text-yellow-800", icon: "⏳" },
  "Awaiting Payment Reference": {
    className: "bg-orange-100 text-orange-800",
    icon: "💳",
  },
  Approved: { className: "bg-green-100 text-green-800", icon: "✅" },
  Rejected: { className: "bg-red-100 text-red-800", icon: "❌" },
  Listed: { className: "bg-green-100 text-green-800", icon: "🏛️" },
  Active: { className: "bg-green-100 text-green-800", icon: "✅" },
  "Ready for Allocation": {
    className: "bg-blue-100 text-blue-800",
    icon: "📋",
  },
  "Allocation Uploaded": { className: "bg-blue-100 text-blue-800", icon: "📤" },
  "Allocation Finalized": {
    className: "bg-blue-100 text-blue-800",
    icon: "🎯",
  },
  "Redemption in Process": {
    className: "bg-yellow-100 text-yellow-800",
    icon: "🔄",
  },
  "Redemption Completed": {
    className: "bg-green-100 text-green-800",
    icon: "✅",
  },
  "Burn in Process": { className: "bg-red-100 text-red-800", icon: "🔥" },
  "Burn Completed": { className: "bg-gray-100 text-gray-800", icon: "⚫" },
  Matured: { className: "bg-blue-100 text-blue-800", icon: "🏁" },
};

const getStatusBadge = (status) => {
  const config = STATUS_CONFIG[status] || {
    className: "bg-gray-100 text-gray-800",
    icon: "",
  };
  return (
    <span
      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${config.className}`}
    >
      {config.icon && <span className="mr-1">{config.icon}</span>}
      {status}
    </span>
  );
};

const AssignedBonds = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Fetch assigned bonds for the current trustee
  const { data: assignmentsData, isPending: bondsLoading } = useQuery({
    queryKey: ["trustee-bonds", user?.id, debouncedSearchTerm, statusFilter, currentPage],
    queryFn: () => bondTrusteesAPI.getTrusteeBonds(user.id || user._id),
    enabled: !!user,
    select: (response) => {
      // Extract the assignments array from the response
      const assignments = response?.data?.data || response?.data || [];
      // Ensure it's always an array
      return Array.isArray(assignments) ? assignments : [];
    },
  });

  // Filter and paginate locally
  const allBonds = Array.isArray(assignmentsData)
    ? assignmentsData.map(assignment => assignment.bondId).filter(Boolean)
    : [];

  // Apply search filter
  const searchFiltered = debouncedSearchTerm
    ? allBonds.filter(bond =>
      bond.bondName?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      bond.bondNumber?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    )
    : allBonds;

  // Apply status filter
  const statusFiltered = statusFilter === "all"
    ? searchFiltered
    : searchFiltered.filter(bond => bond.status === statusFilter);

  // Apply pagination
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const bonds = statusFiltered.slice(startIndex, startIndex + PAGE_SIZE);
  const totalBonds = statusFiltered.length;
  const totalPages = Math.max(1, Math.ceil(totalBonds / PAGE_SIZE));

  if (bondsLoading) {
    return <LoadingSpinner size="lg" text="Loading assigned bonds..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Assigned Bonds
              </h1>
              <p className="text-sm text-gray-500">
                View and manage bonds assigned to you
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search bonds..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Listed">Listed</option>
            <option value="Pending Review">Pending Review</option>
            <option value="Approved">Approved</option>
            <option value="Draft">Draft</option>
            <option value="Matured">Matured</option>
          </select>
        </div>

        {/* Bonds Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {bonds.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>No bonds assigned</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bond Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Issue Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Maturity Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Holders
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bonds.map((bond) => (
                      <tr key={bond._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {bond.bondName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {bond.issueDate ? formatDate(bond.issueDate) : "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {bond.maturityDate
                            ? formatDate(bond.maturityDate)
                            : "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(bond.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Users className="h-4 w-4 mr-1" />
                            {bond.tokenHolders || 0}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() =>
                              navigate(`/trustee/bonds/${bond._id}`)
                            }
                            className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing page {currentPage} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignedBonds;
